import React from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import { formatDateTime, timeAgo, topAndTail } from '../utils/stringUtils';
import { DeleteButton } from './shared/DeleteButton';

const ChannelsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, actions, getCurrentStats } = useApp();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'name' | 'status' | 'connections'>('name');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  // Clear any dashboard filters when viewing all channels (only once on mount)
  React.useEffect(() => {
    actions.setDashboardFilters(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const stats = getCurrentStats();
  const channelList = stats?.channels || [];
  const [showPayloadModal, setShowPayloadModal] = React.useState(false);
  const [selectedPayload, setSelectedPayload] = React.useState<any>(null);
  const [gatewayLookup, setGatewayLookup] = React.useState<Map<string, { name: string; channels: Map<string, string> }>>(new Map());
  const [recentActivityFilters, setRecentActivityFilters] = React.useState<{
    a2a: boolean;
    ap2: boolean;
    ucp: boolean;
    mcp: boolean;
    mpx: boolean;
  }>({ a2a: false, ap2: false, ucp: false, mcp: false, mpx: false });

  // Copy deep link to clipboard
  const copyDeepLink = (configId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#/channels/view/${encodeURIComponent(configId)}`;
    navigator.clipboard.writeText(url);
  };

  // Get badge variant based on status
  const getStatusBadgeVariant = (status?: string): string => {
    if (!status) return 'badge-secondary';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('error') || lowerStatus.includes('fail') || lowerStatus.includes('fault')) {
      return 'badge-danger';
    }
    if (lowerStatus.includes('success') || lowerStatus === 'ok') {
      return 'badge-success';
    }
    return 'badge-secondary';
  };

  // Check if channel has activity in the last 5 minutes
  const hasRecentActivity = (channel: any): boolean => {
    if (!channel.last_activity) return false;
    const lastActivity = new Date(channel.last_activity);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return lastActivity >= fiveMinutesAgo;
  };

  // Toggle recent activity filter for a protocol
  const toggleRecentActivityFilter = (protocol: 'a2a' | 'ap2' | 'ucp' | 'mcp' | 'mpx') => {
    setRecentActivityFilters(prev => ({
      ...prev,
      [protocol]: !prev[protocol]
    }));
  };

  const { a2aChannels, ap2Channels, ucpChannels, mcpChannels, mpxChannels } = React.useMemo(() => {
    const channelList = stats?.channels || [];
    
    // Separate channels by protocol and extension
    // UCP channels are A2A channels with UCP extension
    const a2a = channelList.filter(ch => 
      ch.protocol === 'a2a' && 
      !ch.primary_extension?.includes('ucp.dev') &&
      !ch.supported_extensions?.some((ext: string) => ext.includes('ucp.dev'))
    );
    const ap2 = channelList.filter(ch => ch.protocol === 'ap2');
    const ucp = channelList.filter(ch => 
      ch.protocol === 'a2a' &&
      (ch.primary_extension?.includes('ucp.dev') || 
       ch.supported_extensions?.some((ext: string) => ext.includes('ucp.dev')))
    );
    const mcp = channelList.filter(ch => ch.protocol === 'mcp');
    const mpx = channelList.filter(ch => ch.protocol === 'mpx');
    
    // Sort each protocol's channels alphabetically by name
    a2a.sort((a, b) => a.name.localeCompare(b.name));
    ap2.sort((a, b) => a.name.localeCompare(b.name));
    ucp.sort((a, b) => a.name.localeCompare(b.name));
    mcp.sort((a, b) => a.name.localeCompare(b.name));
    mpx.sort((a, b) => a.name.localeCompare(b.name));
    
    return {
      a2aChannels: a2a,
      ap2Channels: ap2,
      ucpChannels: ucp,
      mcpChannels: mcp,
      mpxChannels: mpx
    };
  }, [stats?.channels]);

  // Filter channels based on search term and recent activity
  const filterChannels = (channels: any[], protocol: 'a2a' | 'ap2' | 'ucp' | 'mcp' | 'mpx') => {
    let filtered = channels;
    
    // Apply search filter
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      const searchLower = trimmedSearch.toLowerCase();
      filtered = filtered.filter(ch => 
        ch.name?.toLowerCase().includes(searchLower) ||
        ch.description?.toLowerCase().includes(searchLower) ||
        ch.config_id?.toLowerCase().includes(searchLower) ||
        ch.listen_address?.toLowerCase().includes(searchLower) ||
        ch.target_endpoint?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply recent activity filter if active
    if (recentActivityFilters[protocol]) {
      filtered = filtered.filter(ch => hasRecentActivity(ch));
    }
    
    return filtered;
  };

  const filteredA2aChannels = React.useMemo(() => filterChannels(a2aChannels, 'a2a'), [a2aChannels, searchTerm, recentActivityFilters.a2a]);
  const filteredAp2Channels = React.useMemo(() => filterChannels(ap2Channels, 'ap2'), [ap2Channels, searchTerm, recentActivityFilters.ap2]);
  const filteredUcpChannels = React.useMemo(() => filterChannels(ucpChannels, 'ucp'), [ucpChannels, searchTerm, recentActivityFilters.ucp]);
  const filteredMcpChannels = React.useMemo(() => filterChannels(mcpChannels, 'mcp'), [mcpChannels, searchTerm, recentActivityFilters.mcp]);
  const filteredMpxChannels = React.useMemo(() => filterChannels(mpxChannels, 'mpx'), [mpxChannels, searchTerm, recentActivityFilters.mpx]);

  // Fetch gateways for name lookup only (no channel queries needed - we use stored fabric_target_name)
  React.useEffect(() => {
    const fetchGatewayData = async () => {
      try {
        const response = await apiClient.get('/gateways');
        const lookup = new Map<string, { name: string; channels: Map<string, string> }>();
        
        for (const gateway of response.data) {
          // Just store gateway name - no need to query channels anymore
          lookup.set(gateway.id, {
            name: gateway.name,
            channels: new Map() // Empty - we use stored fabric_target_name instead
          });
        }
        
        setGatewayLookup(lookup);
      } catch (error) {
        console.error('Failed to fetch gateway data:', error);
      }
    };
    
    fetchGatewayData();
  }, []);

// Helper to format fabric:// URLs with human-readable names
  const formatFabricUrl = (url: string, fabricTargetName?: string): { display: string; tooltip: string } => {
    if (!url.startsWith('fabric://')) {
      return { display: url, tooltip: url };
    }
    
    const fabricPath = url.substring(9); // Remove 'fabric://'
    const [gatewayId, channelId] = fabricPath.split('/');
    
    const gatewayInfo = gatewayLookup.get(gatewayId);
    const gatewayName = gatewayInfo?.name || `${gatewayId.substring(0, 8)}...`;
    
    // First check if we have a stored fabric_target_name
    if (fabricTargetName) {
      return {
        display: `${gatewayName} → ${fabricTargetName}`,
        tooltip: `fabric://${gatewayId}/${channelId}`
      };
    }
    
    // Then check gatewayLookup
    if (gatewayInfo) {
      const channelName = gatewayInfo.channels.get(channelId);
      if (channelName) {
        return {
          display: `${gatewayName} → ${channelName}`,
          tooltip: `fabric://${gatewayId}/${channelId}`
        };
      }
    }
    
    // Fallback to shortened channel ID
    return {
      display: `${gatewayName} → ${channelId.substring(0, 8)}...`,
      tooltip: `fabric://${gatewayId}/${channelId}`
    };
  };
  

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Channels..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
        <div>
          <button 
            className="btn btn-sm btn-success me-2"
            onClick={() => navigate('/onboard')}
          >
            <i className="fas fa-magic"></i> Onboard Agent
          </button>
          <button 
            className="btn btn-sm btn-primary"
            style={{ marginLeft: '0.5rem' }}
            onClick={() => navigate('/channels/new')}
          >
            <i className="fas fa-plus"></i> Add Channel
          </button>
        </div>
      </div>

      {a2aChannels.length === 0 && ap2Channels.length === 0 && ucpChannels.length === 0 && mcpChannels.length === 0 && mpxChannels.length === 0 ? (
        <div className="card shadow mb-4">
          <div className="card-body text-center">
            <div className="text-muted">
              <i className="fas fa-channel fa-3x mb-3"></i>
              <p>No channels are configured on this Affinidi Trust Fabric Gateway.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* A2A Channels */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary d-flex justify-content-between align-items-center">
                <span>
                  <span className={`badge ml-2 badge-primary`} style={{ fontSize: '1rem'}}>A2A</span> Protocol
                  <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredA2aChannels.length}{searchTerm && ` of ${a2aChannels.length}`}</span>
                </span>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => navigate('/channels/new?protocol=a2a')}
                    title="Add new A2A channel"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-plus"></i> Add A2A Channel </button> <i 
                    className={`fas fa-clock ${recentActivityFilters.a2a ? 'text-primary' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px' }}
                    onClick={() => toggleRecentActivityFilter('a2a')}
                    title={recentActivityFilters.a2a ? 'Show all channels' : 'Show only channels active in last 5 minutes'}
                  ></i>
                </div>
              </h6>
            </div>
            <div className="card-body">
              {a2aChannels.length === 0 ? (
                <div className="text-center">
                  <div className="text-muted">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>No A2A channels configured.</p>
                  </div>
                </div>
              ) : filteredA2aChannels.length === 0 ? (
                  <div className="text-center">
                    <div className="text-muted">
                      <i className="fas fa-search fa-3x mb-3"></i>
                      <p>No A2A channels match your search.</p>
                    </div>
                  </div>
                ) : (                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th style={{width: '20%'}}>Channel</th>
                        <th style={{width: '35%'}}>Source</th>
                        <th style={{width: '35%'}}>Target</th>
                        <th style={{width: '10%'}}>Last used</th>
                        {/* <th className="text-center">Identities</th> */}
                        {/* <th className="text-center">Metrics</th> */}
                        {/* <th className="text-center">Actions</th>
                        <th style={{width: '40px'}}></th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredA2aChannels.map((channel) => {
                    // Use actual identity count from backend
                    const actualIdentityCount = channel.identity_count || 0;
                    
                    return (
                      <tr 
                        key={channel.config_id} 
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/channels/${encodeURIComponent(channel.config_id)}`)}
                      >
                        <td>
                          <div><strong>{channel.name}</strong></div>
                          <div   style={{ fontSize: '0.8rem' }}><i>{channel.description}</i></div>
                          {!channel.enabled && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-power-off"></i> DISABLED
                            </span>
                          )}
                          {channel.channel_type === 'onboarding' && (
                            <span className="badge badge-info ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-user-plus"></i> ONBOARDING
                            </span>
                          )}
                          {channel.channel_type === 'system' && (
                            <span className="badge badge-secondary ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-cog"></i> SYSTEM
                            </span>
                          )}
                          {channel.channel_type === 'transit' && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-exchange-alt"></i> TRANSIT
                            </span>
                          )}
                          <div className={`badge ${getStatusBadgeVariant(channel.last_status)}`}>{channel.last_status?.toUpperCase()}</div>

                        </td>
                        <td>
                          <code style={{ fontSize: '0.75rem' }}>
                            {channel.listen_address.startsWith('did:') 
                              ? topAndTail(channel.listen_address, 16, 24)
                              : channel.listen_address}{channel.route && channel.route !== '/' ? channel.route : ''}
                          </code>
                        </td>
                        <td>
                          <code 
                            style={{ fontSize: '0.75rem' }}
                            title={channel.target_endpoint.startsWith('fabric://') 
                              ? formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).tooltip 
                              : channel.target_endpoint}
                          >
                            {channel.target_endpoint.startsWith('fabric://') ? (
                              <>
                                <i className="fas fa-project-diagram" style={{ marginRight: '0.3rem' }}></i>
                                {formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).display}
                              </>
                            ) : channel.target_endpoint.startsWith('did:') ? (
                              topAndTail(channel.target_endpoint, 16, 24)
                            ) : (
                              channel.target_endpoint
                            )}
                          </code>
                        </td>
                        {/* <td className="text-center">
                          {actualIdentityCount > 0 ? (
                            <span title={`${actualIdentityCount} unique agent identities extracted`}>
                              {actualIdentityCount}
                            </span>
                          ) : (
                            <span className="text-muted">(None)</span>
                          )}
                        </td> */}
                        {/* <td className="text-center">
                          {(channel.accept_count || 0) > 0 ? (
                            <span className="text-success">
                              <i className="fas fa-check"></i> {channel.accept_count || 0}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                          <br/>
                          {(channel.deny_count || 0) > 0 ? (
                            <span 
                              className="text-warning cursor-pointer"
                              title="Click to view validation failures in capture page"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                              }}
                            >
                              <i className="fas fa-exclamation-triangle"></i> {channel.deny_count || 0}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                          <br/>
                          {(channel.gateway_faults || 0) > 0 ? (
                            <span 
                              className="text-danger cursor-pointer"
                              title="Click to view gateway errors in capture page"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                              }}
                            >
                              <i className="fas fa-times-circle"></i> {channel.gateway_faults || 0}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td> */}
                        {/* <td className="text-center">
                          <button 
                            className="btn btn-sm btn-outline-secondary mr-1"
                            title="Edit this channel"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/channels/${encodeURIComponent(channel.config_id)}`);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-info mr-1"
                            title="Capture live agent payloads for this channel"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                            }}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-primary mr-1"
                            title="Reload this channel"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await apiClient.reloadChannel(channel.name);
                                // Optionally show a success message
                              } catch (error) {
                                console.error('Failed to reload channel:', error);
                                // Optionally show an error message
                              }
                            }}
                          >
                            <i className="fas fa-sync-alt"></i>
                          </button>
                          <DeleteButton
                            onDelete={async () => {
                              try {
                                await apiClient.deleteChannel(channel.config_id);
                                // Remove the channel from the app state immediately
                                actions.removeChannel(channel.config_id);
                              } catch (error) {
                                console.error('Failed to delete channel:', error);
                                alert('Failed to delete channel. Please try again.');
                              }
                            }}
                            className="btn-sm"
                            title="Delete this channel"
                          />
                        </td> */}
                        {/* <td>
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={(e) => copyDeepLink(channel.config_id, e)}
                            title="Copy link to this channel"
                          >
                            <i className="fas fa-link text-muted"></i>
                          </button>
                        </td> */}
                      <td><div style={{ fontSize: '0.8rem'}}>
                        {channel.last_activity ? timeAgo(channel.last_activity) : <span className="text-muted"></span>}
                        </div>
                      </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>

        {/* UCP Channels */}
        <div className="card shadow mb-4">
          <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary d-flex justify-content-between align-items-center">
                <span>
                  <span className={`badge ml-2 badge-success`} style={{ fontSize: '1rem' }}>UCP</span> UCP Protocol
                  <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredUcpChannels.length}{searchTerm && ` of ${ucpChannels.length}`}</span>
                </span>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-sm btn-success me-2"
                    onClick={() => navigate('/channels/new?protocol=a2a&extension=ucp')}
                    title="Add new UCP channel (A2A with UCP extension)"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-plus"></i> Add UCP Channel </button> <i 
                    className={`fas fa-clock ${recentActivityFilters.ucp ? 'text-success' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px' }}
                    onClick={() => toggleRecentActivityFilter('ucp')}
                    title={recentActivityFilters.ucp ? 'Show all channels' : 'Show only channels active in last 5 minutes'}
                  ></i>
                </div>
              </h6>
            </div>
            <div className="card-body">
              {ucpChannels.length === 0 ? (
                <div className="text-center">
                  <div className="text-muted">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>No UCP channels configured.</p>
                  </div>
                </div>
              ) : filteredUcpChannels.length === 0 ? (
                  <div className="text-center">
                    <div className="text-muted">
                      <i className="fas fa-search fa-3x mb-3"></i>
                      <p>No UCP channels match your search.</p>
                    </div>
                  </div>
                ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th style={{width: '20%'}}>Channel</th>
                        <th style={{width: '35%'}}>Source</th>
                        <th style={{width: '35%'}}>Target</th>
                        <th style={{width: '10%'}}>Last used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUcpChannels.map((channel) => {
                    const actualIdentityCount = channel.identity_count || 0;
                    
                    return (
                      <tr 
                        key={channel.config_id} 
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/channels/${encodeURIComponent(channel.config_id)}`)}
                      >
                        <td>
                          <div><strong>{channel.name}</strong></div>
                          {!channel.enabled && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-power-off"></i> DISABLED
                            </span>
                          )}
                          {channel.channel_type === 'onboarding' && (
                            <span className="badge badge-secondary ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-user-plus"></i> ONBOARDING
                            </span>
                          )}
                        </td>
                        <td>
                          <code style={{ fontSize: '0.75rem' }}>
                            {channel.listen_address.startsWith('did:') 
                              ? topAndTail(channel.listen_address, 16, 24)
                              : channel.listen_address}{channel.route && channel.route !== '/' ? channel.route : ''}
                          </code>
                        </td>
                        <td>
                          <code 
                            style={{ fontSize: '0.75rem' }}
                            title={channel.target_endpoint.startsWith('fabric://') 
                              ? formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).tooltip 
                              : channel.target_endpoint}
                          >
                            {channel.target_endpoint.startsWith('fabric://') ? (
                              <>
                                <i className="fas fa-project-diagram" style={{ marginRight: '0.3rem' }}></i>
                                {formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).display}
                              </>
                            ) : channel.target_endpoint.startsWith('did:') ? (
                              topAndTail(channel.target_endpoint, 16, 24)
                            ) : (
                              channel.target_endpoint
                            )}
                          </code>
                        </td>
                        <td>
                          {channel.last_used_at ? (
                          <div>
                            <small className="text-muted" title={new Date(channel.last_used_at * 1000).toLocaleString()}>
                              {timeAgo(new Date(channel.last_used_at * 1000))}
                            </small>
                          </div>
                          ) : (
                          <small className="text-muted">Never</small>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        {/* AP2 Channels */}
        <div className="card shadow mb-4">
          <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary d-flex justify-content-between align-items-center">
                <span>
                  <span className={`badge ml-2 badge-info`} style={{ fontSize: '1rem' }}>AP2</span> AP2 Protocol
                  <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredAp2Channels.length}{searchTerm && ` of ${ap2Channels.length}`}</span>
                </span>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-sm btn-info me-2"
                    onClick={() => navigate('/channels/new?protocol=ap2')}
                    title="Add new AP2 channel"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-plus"></i> Add AP2 Channel </button> <i 
                    className={`fas fa-clock ${recentActivityFilters.ap2 ? 'text-info' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px' }}
                    onClick={() => toggleRecentActivityFilter('ap2')}
                    title={recentActivityFilters.ap2 ? 'Show all channels' : 'Show only channels active in last 5 minutes'}
                  ></i>
                </div>
              </h6>
            </div>
            <div className="card-body">
              {ap2Channels.length === 0 ? (
                <div className="text-center">
                  <div className="text-muted">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>No AP2 channels configured.</p>
                  </div>
                </div>
              ) : filteredAp2Channels.length === 0 ? (
                  <div className="text-center">
                    <div className="text-muted">
                      <i className="fas fa-search fa-3x mb-3"></i>
                      <p>No AP2 channels match your search.</p>
                    </div>
                  </div>
                ) : (                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th style={{width: '20%'}}>Channel</th>
                        <th style={{width: '35%'}}>Source</th>
                        <th style={{width: '35%'}}>Target</th>
                        <th style={{width: '10%'}}>Last used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAp2Channels.map((channel) => {
                    // Use actual identity count from backend
                    const actualIdentityCount = channel.identity_count || 0;
                    
                    return (
                      <tr 
                        key={channel.config_id} 
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/channels/${encodeURIComponent(channel.config_id)}`)}
                      >
                        <td>
                          <div><strong>{channel.name}</strong></div>
                          {!channel.enabled && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-power-off"></i> DISABLED
                            </span>
                          )}
                          {channel.channel_type === 'onboarding' && (
                            <span className="badge badge-info ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-user-plus"></i> ONBOARDING
                            </span>
                          )}
                          {channel.channel_type === 'system' && (
                            <span className="badge badge-secondary ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-cog"></i> SYSTEM
                            </span>
                          )}
                          {channel.channel_type === 'transit' && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-exchange-alt"></i> TRANSIT
                            </span>
                          )}
                          <div style={{ fontSize: '0.8rem'}}><i>{channel.description}</i></div>

                          <div className={`badge ${getStatusBadgeVariant(channel.last_status)}`}>{channel.last_status?.toUpperCase()}</div>

                        </td>
                        
                        <td>
                          <code style={{ fontSize: '0.75rem' }}>
                            {channel.listen_address.startsWith('did:') 
                              ? topAndTail(channel.listen_address, 16, 24)
                              : channel.listen_address}{channel.route && channel.route !== '/' ? channel.route : ''}
                          </code>
                        </td>
                        <td>
                          <code 
                            style={{ fontSize: '0.75rem' }}
                            title={channel.target_endpoint.startsWith('fabric://') 
                              ? formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).tooltip 
                              : channel.target_endpoint}
                          >
                            {channel.target_endpoint.startsWith('fabric://') ? (
                              <>
                                <i className="fas fa-project-diagram" style={{ marginRight: '0.3rem' }}></i>
                                {formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).display}
                              </>
                            ) : channel.target_endpoint.startsWith('did:') ? (
                              topAndTail(channel.target_endpoint, 16, 24)
                            ) : (
                              channel.target_endpoint
                            )}
                          </code>
                        </td>
                        <td style={{ fontSize: '0.75rem' }}>
                          {channel.last_used_at ? (
                            <span className={hasRecentActivity(channel) ? 'text-success font-weight-bold' : ''}>
                              {timeAgo(new Date(channel.last_used_at))}
                            </span>
                          ) : (
                            <span className="text-muted">Never</span>
                          )}
                      </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>

        {/* MCP Channels */}
        <div className="card shadow mb-4">
          <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary d-flex justify-content-between align-items-center">
                <span>
                  <span className={`badge ml-2 badge-success`} style={{ fontSize: '1rem' }}>MCP</span> Protocol
                  <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredMcpChannels.length}{searchTerm && ` of ${mcpChannels.length}`}</span>
                </span>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-sm btn-success me-2"
                    onClick={() => navigate('/channels/new?protocol=mcp')}
                    title="Add new MCP channel"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-plus"></i> Add MCP Channel </button> <i 
                    className={`fas fa-clock ${recentActivityFilters.mcp ? 'text-success' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px' }}
                    onClick={() => toggleRecentActivityFilter('mcp')}
                    title={recentActivityFilters.mcp ? 'Show all channels' : 'Show only channels active in last 5 minutes'}
                  ></i>
                </div>
              </h6>
          </div>
          <div className="card-body">
            {mcpChannels.length === 0 ? (
              <div className="text-center">
                <div className="text-muted">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>No MCP channels configured.</p>
                </div>
              </div>
            ) : filteredMcpChannels.length === 0 ? (
                <div className="text-center">
                  <div className="text-muted">
                    <i className="fas fa-search fa-3x mb-3"></i>
                    <p>No MCP channels match your search.</p>
                  </div>
                </div>
              ) : (
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th style={{width: '20%'}}>Channel</th>
                      <th style={{width: '35%'}}>Source</th>
                      <th style={{width: '35%'}}>Target</th>
                      <th style={{width: '10%'}}>Last used</th>
                      {/* <th className="text-center">Identities</th> */}
                      {/* <th className="text-center">Metrics</th> */}
                      {/* <th className="text-center">Actions</th> */}
                      {/* <th style={{width: '40px'}}></th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMcpChannels.map((channel) => {
                      // Use actual identity count from backend
                      const actualIdentityCount = channel.identity_count || 0;
                      
                      return (
                        <tr 
                          key={channel.config_id} 
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/channels/${encodeURIComponent(channel.config_id)}`)}
                        >
                          <td>
                            <strong>{channel.name}</strong>
                            {!channel.enabled && (
                              <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-power-off"></i> DISABLED
                              </span>
                            )}
                            {channel.channel_type === 'onboarding' && (
                              <span className="badge badge-info ml-2" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-user-plus"></i> ONBOARDING
                              </span>
                            )}
                            {channel.channel_type === 'system' && (
                              <span className="badge badge-secondary ml-2" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-cog"></i> SYSTEM
                              </span>
                            )}
                            {channel.channel_type === 'transit' && (
                              <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-exchange-alt"></i> TRANSIT
                              </span>
                            )}
                            <div style={{ fontSize: '0.8rem'}}><i>{channel.description}</i></div>

                            <div className={`badge ${getStatusBadgeVariant(channel.last_status)}`}>{channel.last_status?.toUpperCase()}</div>

                          </td>
                          
                          <td>
                            <code style={{ fontSize: '0.75rem' }}>
                              {channel.listen_address.startsWith('did:') 
                                ? topAndTail(channel.listen_address, 16, 24)
                                : channel.listen_address}{channel.route && channel.route !== '/' ? channel.route : ''}
                            </code>
                          </td>
                          <td>
                            <code 
                              style={{ fontSize: '0.75rem' }}
                              title={channel.target_endpoint.startsWith('fabric://') 
                                ? formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).tooltip 
                                : channel.target_endpoint}
                            >
                              {channel.target_endpoint.startsWith('fabric://') ? (
                                <>
                                  <i className="fas fa-project-diagram" style={{ marginRight: '0.3rem' }}></i>
                                  {formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).display}
                                </>
                              ) : channel.target_endpoint.startsWith('did:') ? (
                                topAndTail(channel.target_endpoint, 16, 24)
                              ) : (
                                channel.target_endpoint
                              )}
                            </code>
                          </td>
                          {/* <td className="text-center">
                            {actualIdentityCount > 0 ? (
                              <span title={`${actualIdentityCount} unique agent identities extracted`}>
                                {actualIdentityCount}
                              </span>
                            ) : (
                              <span className="text-muted">(None)</span>
                            )}
                          </td> */}
                          {/* <td className="text-center">
                            {(channel.accept_count || 0) > 0 ? (
                              <span className="text-success">
                                <i className="fas fa-check"></i> {channel.accept_count || 0}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                            <br/>
                            {(channel.deny_count || 0) > 0 ? (
                              <span 
                                className="text-warning cursor-pointer"
                                title="Click to view validation failures in capture page"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                                }}
                              >
                                <i className="fas fa-exclamation-triangle"></i> {channel.deny_count || 0}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                            <br/>
                            {(channel.gateway_faults || 0) > 0 ? (
                              <span 
                                className="text-danger cursor-pointer"
                                title="Click to view gateway errors in capture page"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                                }}
                              >
                                <i className="fas fa-times-circle"></i> {channel.gateway_faults || 0}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td> */}
                          {/* <td className="text-center">
                            <button 
                              className="btn btn-sm btn-outline-secondary mr-1"
                              title="Edit this channel"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/channels/${encodeURIComponent(channel.config_id)}`);
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-info mr-1"
                              title="Capture live agent payloads for this channel"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/channels/${encodeURIComponent(channel.config_id)}/capture`);
                              }}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-primary mr-1"
                              title="Reload this channel"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await apiClient.reloadChannel(channel.name);
                                  // Optionally show a success message
                                } catch (error) {
                                  console.error('Failed to reload channel:', error);
                                  // Optionally show an error message
                                }
                              }}
                            >
                              <i className="fas fa-sync-alt"></i>
                            </button>
                            <DeleteButton
                              onDelete={async () => {
                                try {
                                  await apiClient.deleteChannel(channel.config_id);
                                  // Remove the channel from the app state immediately
                                  actions.removeChannel(channel.config_id);
                                } catch (error) {
                                  console.error('Failed to delete channel:', error);
                                  alert('Failed to delete channel. Please try again.');
                                }
                              }}
                              className="btn-sm"
                              title="Delete this channel"
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-link p-0"
                              onClick={(e) => copyDeepLink(channel.config_id, e)}
                              title="Copy link to this channel"
                            >
                              <i className="fas fa-link text-muted"></i>
                            </button>
                          </td> */}
                      <td><div style={{ fontSize: '0.8rem'}}>
                        {channel.last_activity ? timeAgo(channel.last_activity) : <span className="text-muted"></span>}
                        </div>
                      </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>

        {/* MPX Channels */}
        <div className="card shadow mb-4">
          <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary d-flex justify-content-between align-items-center">
                <span>
                  <span className={`badge ml-2 badge-info`} style={{ fontSize: '1rem' }}>MPX</span> Protocol
                  <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{filteredMpxChannels.length}{searchTerm && ` of ${mpxChannels.length}`}</span>
                </span>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-sm btn-info me-2"
                    onClick={() => navigate('/channels/new/mpx')}
                    title="Add new MPX channel"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-plus"></i> Add MPX Channel </button> <i 
                    className={`fas fa-clock ${recentActivityFilters.mpx ? 'text-info' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px' }}
                    onClick={() => toggleRecentActivityFilter('mpx')}
                    title={recentActivityFilters.mpx ? 'Show only channels active in last 5 minutes' : 'Show all channels'}
                  ></i>
                </div>
              </h6>
          </div>
          <div className="card-body">
            {mpxChannels.length === 0 ? (
              <div className="text-center">
                <div className="text-muted">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>No MPX channels configured.</p>
                </div>
              </div>
            ) : filteredMpxChannels.length === 0 ? (
              <div className="text-center">
                <div className="text-muted">
                  <i className="fas fa-search fa-3x mb-3"></i>
                  <p>No MPX channels match your search.</p>
                </div>
              </div>
            ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th style={{width: '20%'}}>Channel</th>
                    <th style={{width: '35%'}}>Source</th>
                    <th style={{width: '35%'}}>Target</th>
                    <th style={{width: '10%'}}>Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMpxChannels.map((channel) => {
                    // Use actual identity count from backend
                    const actualIdentityCount = channel.identity_count || 0;
                    
                    return (
                      <tr 
                        key={channel.config_id} 
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/channels/${encodeURIComponent(channel.config_id)}`)}
                      >
                        <td>
                          <strong>{channel.name}</strong>
                          {!channel.enabled && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-power-off"></i> DISABLED
                            </span>
                          )}
                          {channel.channel_type === 'onboarding' && (
                            <span className="badge badge-info ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-user-plus"></i> ONBOARDING
                            </span>
                          )}
                          {channel.channel_type === 'system' && (
                            <span className="badge badge-secondary ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-cog"></i> SYSTEM
                            </span>
                          )}
                          {channel.channel_type === 'transit' && (
                            <span className="badge badge-warning ml-2" style={{ fontSize: '0.65rem' }}>
                              <i className="fas fa-exchange-alt"></i> TRANSIT
                            </span>
                          )}
                          <div style={{ fontSize: '0.8rem'}}><i>{channel.description}</i></div>
                          <div className={`badge ${getStatusBadgeVariant(channel.last_status)}`}>{channel.last_status?.toUpperCase()}</div>
                        </td>
                        <td>
                          <code style={{ fontSize: '0.75rem' }}>
                            {channel.listen_address.startsWith('did:') 
                              ? topAndTail(channel.listen_address, 16, 24)
                              : channel.listen_address}{channel.route && channel.route !== '/' ? channel.route : ''}
                          </code>
                        </td>
                        <td>
                          <code 
                            style={{ fontSize: '0.75rem' }}
                            title={channel.target_endpoint.startsWith('fabric://') 
                              ? formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).tooltip 
                              : channel.target_endpoint}
                          >
                            {channel.target_endpoint.startsWith('fabric://') ? (
                              <>
                                <i className="fas fa-project-diagram" style={{ marginRight: '0.3rem' }}></i>
                                {formatFabricUrl(channel.target_endpoint, channel.fabric_target_name).display}
                              </>
                            ) : channel.target_endpoint.startsWith('did:') ? (
                              topAndTail(channel.target_endpoint, 16, 24)
                            ) : channel.target_endpoint.startsWith('proxy://') ? (
                              <>
                                <i className="fas fa-plug" style={{ marginRight: '0.3rem' }}></i>
                                {channel.target_endpoint.substring(8)}
                              </>
                            ) : (
                              channel.target_endpoint
                            )}
                          </code>
                        </td>
                        <td><div style={{ fontSize: '0.8rem'}}>
                          {channel.last_activity ? timeAgo(channel.last_activity) : <span className="text-muted"></span>}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>

      </>
      )}

      {/* Payload Modal */}
      {showPayloadModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowPayloadModal(false)}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-file-code"></i> Latest Payload
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowPayloadModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '1rem', 
                  borderRadius: '4px',
                  maxHeight: '500px',
                  overflow: 'auto'
                }}>
                  <code>{JSON.stringify(selectedPayload, null, 2)}</code>
                </pre>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowPayloadModal(false)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(selectedPayload, null, 2));
                  }}
                >
                  <i className="fas fa-copy"></i> Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelsPage;